import {
  queryMultilingualAttributes,
  mutationUpdateLanguageStatus,
  mapQueryMultilingualAttributes,
  mapMutationUpdateLanguageStatus,
  MultilingualAttribute,
  UpdateLanguageStatusParams
} from './multilingualQueries'
import graphqlService from '../../services/graphqlService'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// Types
export interface MultilingualState {
  attributes: MultilingualAttribute[]
  loading: boolean
  error: string | null
}

// Initial state
export const initialState: MultilingualState = {
  attributes: [],
  loading: false,
  error: null
}

// Async thunks
export const ACTION_FETCH_MULTILINGUAL_ATTRIBUTES = 'multilingual/fetchAttributes'
export const fetchMultilingualAttributes = createAsyncThunk(
  ACTION_FETCH_MULTILINGUAL_ATTRIBUTES,
  async (_, { rejectWithValue }) => {
    try {
      const query = queryMultilingualAttributes()
      const result = await graphqlService.execute(query)
      return mapQueryMultilingualAttributes(result)
    } catch (error: any) {
      return rejectWithValue({
        statusMessage: error?.errors?.[0]?.message || 'Failed to fetch multilingual attributes'
      })
    }
  }
)

export const ACTION_UPDATE_LANGUAGE_STATUS = 'multilingual/updateLanguageStatus'
export const updateLanguageStatus = createAsyncThunk(
  ACTION_UPDATE_LANGUAGE_STATUS,
  async (params: UpdateLanguageStatusParams) => {
    const mutation = mutationUpdateLanguageStatus(params)
    const result = await graphqlService.execute(mutation)
    const mappedResponse = mapMutationUpdateLanguageStatus(result)

    return mappedResponse
  }
)

// Slice
const multilingualSlice = createSlice({
  name: 'multilingual',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    resetMultilingual: () => initialState
  },
  extraReducers: (builder) => {
    // Fetch multilingual attributes
    builder
      .addCase(fetchMultilingualAttributes.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMultilingualAttributes.fulfilled, (state, action) => {
        state.loading = false
        state.attributes = action.payload as MultilingualAttribute[]
      })
      .addCase(fetchMultilingualAttributes.rejected, (state, action: any) => {
        state.loading = false
        state.error = action.payload?.statusMessage || 'Failed to fetch multilingual attributes'
      })
  }
})

export const { clearError, resetMultilingual } = multilingualSlice.actions

export default multilingualSlice.reducer
